import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Input } from './ui/input';
import { Search, ChevronDown, Check, X } from 'lucide-react';
import { VEHICLE_TYPES, VEHICLE_CATEGORIES } from '../constants/vehicleTypes';
import { cn } from '../lib/utils';

const VehicleSearchInput = ({ value, onChange, placeholder = "Rechercher un type de véhicule..." }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [localSearchTerm, setLocalSearchTerm] = useState('');
  const [hasUserTyped, setHasUserTyped] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Calculate selected type based on value
  const selectedType = useMemo(() => {
    if (!value) return null;
    return VEHICLE_TYPES.find(t => t.value === value || t.label.toLowerCase() === value.toLowerCase()) || null;
  }, [value]);

  // Display text: user's local input or derived from value
  const displayText = useMemo(() => {
    if (hasUserTyped) return localSearchTerm;
    if (selectedType) return selectedType.label;
    if (value) return value;
    return '';
  }, [hasUserTyped, localSearchTerm, selectedType, value]);

  // Is custom type (not in the list)
  const isCustom = value && !selectedType;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setHasUserTyped(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter vehicles based on search term
  const searchTerm = hasUserTyped ? localSearchTerm : displayText;
  const filteredVehicles = VEHICLE_TYPES.filter(vehicle => 
    vehicle.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group filtered vehicles by category
  const groupedVehicles = VEHICLE_CATEGORIES.map(category => ({
    ...category,
    types: filteredVehicles.filter(v => v.category === category.value)
  })).filter(group => group.types.length > 0);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setLocalSearchTerm(newValue);
    setHasUserTyped(true);
    setIsOpen(true);
    onChange(newValue);
  };

  const handleSelect = (vehicle) => {
    setLocalSearchTerm(vehicle.label);
    setHasUserTyped(false);
    setIsOpen(false);
    onChange(vehicle.value);
  };

  const handleClear = () => {
    setLocalSearchTerm('');
    setHasUserTyped(false);
    onChange('');
    inputRef.current?.focus();
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleUseCustom = () => {
    setHasUserTyped(false);
    setIsOpen(false);
    onChange(localSearchTerm);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          ref={inputRef}
          type="text"
          value={displayText}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          className="pl-10 pr-20"
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {displayText && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <X className="h-4 w-4 text-gray-400" />
            </button>
          )}
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <ChevronDown className={cn(
              "h-4 w-4 text-gray-400 transition-transform",
              isOpen && "rotate-180"
            )} />
          </button>
        </div>
      </div>

      {/* Selected type indicator */}
      {selectedType && !hasUserTyped && (
        <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
          <span className="text-lg">{selectedType.icon}</span>
          <span className="text-sm font-medium text-green-800">{selectedType.label}</span>
          <Check className="h-4 w-4 text-green-600 ml-auto" />
        </div>
      )}

      {/* Custom input indicator */}
      {isCustom && !hasUserTyped && (
        <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
          <span className="text-lg">✏️</span>
          <span className="text-sm text-blue-800">
            Type personnalisé : <strong>{value}</strong>
          </span>
        </div>
      )}

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full max-h-80 overflow-auto bg-white border border-gray-200 rounded-lg shadow-lg">
          {filteredVehicles.length === 0 ? (
            <div className="p-4 text-center">
              <p className="text-gray-500 text-sm">Aucun véhicule trouvé pour &quot;{searchTerm}&quot;</p>
              <p className="text-xs text-gray-400 mt-1">
                Votre saisie sera utilisée comme type personnalisé
              </p>
              {searchTerm && (
                <button
                  type="button"
                  onClick={handleUseCustom}
                  className="mt-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm hover:bg-blue-200"
                >
                  ✏️ Utiliser &quot;{searchTerm}&quot;
                </button>
              )}
            </div>
          ) : (
            <>
              {groupedVehicles.map(group => (
                <div key={group.value}>
                  {/* Category header */}
                  <div className="px-3 py-2 bg-gray-50 border-b border-gray-100 sticky top-0">
                    <span className="text-xs font-bold text-gray-600 uppercase tracking-wide">
                      {group.label}
                    </span>
                  </div>
                  
                  {/* Items in category */}
                  {group.types.map(vehicle => (
                    <button
                      key={vehicle.value}
                      type="button"
                      onClick={() => handleSelect(vehicle)}
                      className={cn(
                        "w-full px-3 py-2 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left",
                        selectedType?.value === vehicle.value && "bg-green-50"
                      )}
                    >
                      <span className="text-xl">{vehicle.icon}</span>
                      <span className="flex-1 text-sm">{vehicle.label}</span>
                      {selectedType?.value === vehicle.value && (
                        <Check className="h-4 w-4 text-green-600" />
                      )}
                    </button>
                  ))}
                </div>
              ))}
              
              {/* Custom option at the bottom */}
              {searchTerm && !filteredVehicles.some(v => v.label.toLowerCase() === searchTerm.toLowerCase()) && (
                <div className="border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleUseCustom}
                    className="w-full px-3 py-3 flex items-center gap-3 hover:bg-blue-50 transition-colors text-left"
                  >
                    <span className="text-xl">✏️</span>
                    <div>
                      <span className="text-sm font-medium text-blue-700">
                        Utiliser &quot;{searchTerm}&quot; comme type personnalisé
                      </span>
                      <p className="text-xs text-gray-500">
                        Si votre type de véhicule n&apos;est pas dans la liste
                      </p>
                    </div>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default VehicleSearchInput;
